import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Shield,
  Users,
  Calendar,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Upload,
  FileText,
  RefreshCw,
  Lock,
  Unlock,
  Clock,
  Package,
  AlertOctagon,
  Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../components/ui/alert';

const API = process.env.REACT_APP_BACKEND_URL;

const LicenseManagement = () => {
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [enforcementStatus, setEnforcementStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchLicenseStatus();
  }, []);

  const fetchLicenseStatus = async () => {
    try {
      const token = localStorage.getItem('hrms_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statusRes, enforcementRes, eventsRes] = await Promise.all([
        axios.get(`${API}/api/license/enforcement/status`),
        axios.get(`${API}/api/license/enforcement/details`, { headers }),
        axios.get(`${API}/api/license/enforcement/events`, { headers })
      ]);

      setLicenseStatus(statusRes.data);
      setEnforcementStatus(enforcementRes.data);
      setEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error('Error fetching license status:', error);
      // Still try to get public status
      try {
        const statusRes = await axios.get(`${API}/api/license/enforcement/status`);
        setLicenseStatus(statusRes.data);
      } catch (e) {
        toast.error('Failed to fetch license status');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUploadLicense = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const content = await file.text();
      const token = localStorage.getItem('hrms_token');

      const response = await axios.post(
        `${API}/api/license/enforcement/upload`,
        { license_data: content },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('License uploaded successfully!');
      fetchLicenseStatus();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to upload license';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'valid':
        return 'text-emerald-500';
      case 'expired_support':
        return 'text-yellow-500';
      case 'user_limit_exceeded':
        return 'text-orange-500';
      case 'invalid_signature':
      case 'tampered':
      case 'restricted_mode':
        return 'text-red-500';
      default:
        return 'text-slate-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid':
        return <CheckCircle className="h-6 w-6 text-emerald-500" />;
      case 'expired_support':
        return <Clock className="h-6 w-6 text-yellow-500" />;
      case 'user_limit_exceeded':
        return <AlertTriangle className="h-6 w-6 text-orange-500" />;
      case 'invalid_signature':
      case 'tampered':
      case 'restricted_mode':
        return <XCircle className="h-6 w-6 text-red-500" />;
      default:
        return <AlertOctagon className="h-6 w-6 text-slate-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="license-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">License Management</h1>
          <p className="text-slate-400">PSCL User Limit Enforcement</p>
        </div>
        <Button
          onClick={() => fetchLicenseStatus()}
          variant="outline"
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Restricted Mode Warning */}
      {licenseStatus?.restricted_mode && (
        <Alert variant="destructive" className="bg-red-900/50 border-red-600">
          <AlertOctagon className="h-5 w-5" />
          <AlertTitle>System in Restricted Mode</AlertTitle>
          <AlertDescription>
            License integrity issue detected. User creation is blocked. Contact vendor support.
          </AlertDescription>
        </Alert>
      )}

      {/* User Limit Warning */}
      {licenseStatus?.status === 'user_limit_exceeded' && (
        <Alert className="bg-orange-900/30 border-orange-600">
          <AlertTriangle className="h-5 w-5 text-orange-500" />
          <AlertTitle className="text-orange-400">User Limit Exceeded</AlertTitle>
          <AlertDescription className="text-orange-300">
            Current users ({licenseStatus.current_users}) exceed the licensed limit ({licenseStatus.max_users}).
            New user creation is blocked. Please upgrade your license.
          </AlertDescription>
        </Alert>
      )}

      {/* Support Expiry Warning */}
      {licenseStatus?.status === 'expired_support' && (
        <Alert className="bg-yellow-900/30 border-yellow-600">
          <Clock className="h-5 w-5 text-yellow-500" />
          <AlertTitle className="text-yellow-400">Support Expired</AlertTitle>
          <AlertDescription className="text-yellow-300">
            Your support subscription has expired. System continues to function, but updates and patches are unavailable.
          </AlertDescription>
        </Alert>
      )}

      {/* License Status Card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-400">License Status</CardTitle>
            {getStatusIcon(licenseStatus?.status)}
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold capitalize ${getStatusColor(licenseStatus?.status)}`}>
              {licenseStatus?.status?.replace(/_/g, ' ') || 'Unknown'}
            </div>
            <p className="text-sm text-slate-400 mt-1">{licenseStatus?.message}</p>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-400">User Capacity</CardTitle>
            <Users className="h-5 w-5 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">
              {licenseStatus?.current_users} / {licenseStatus?.max_users}
            </div>
            <div className="mt-2 h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${
                  (licenseStatus?.current_users / licenseStatus?.max_users) > 0.9
                    ? 'bg-red-500'
                    : (licenseStatus?.current_users / licenseStatus?.max_users) > 0.75
                    ? 'bg-yellow-500'
                    : 'bg-emerald-500'
                }`}
                style={{
                  width: `${Math.min(100, (licenseStatus?.current_users / licenseStatus?.max_users) * 100)}%`
                }}
              />
            </div>
            <p className="text-sm text-slate-400 mt-2">
              {licenseStatus?.users_remaining} users remaining
            </p>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-400">Support Status</CardTitle>
            {licenseStatus?.support_active ? (
              <CheckCircle className="h-5 w-5 text-emerald-500" />
            ) : (
              <XCircle className="h-5 w-5 text-red-500" />
            )}
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${licenseStatus?.support_active ? 'text-emerald-400' : 'text-red-400'}`}>
              {licenseStatus?.support_active ? 'Active' : 'Expired'}
            </div>
            {licenseStatus?.support_expiry && (
              <p className="text-sm text-slate-400 mt-1">
                {licenseStatus?.support_active
                  ? `Expires: ${licenseStatus.support_expiry}`
                  : `Expired: ${licenseStatus.support_expiry}`
                }
              </p>
            )}
            {licenseStatus?.days_until_support_expiry !== null && (
              <p className="text-sm text-slate-400">
                {licenseStatus.days_until_support_expiry > 0
                  ? `${licenseStatus.days_until_support_expiry} days remaining`
                  : `Expired ${Math.abs(licenseStatus.days_until_support_expiry)} days ago`
                }
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* License Details & Upload */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Modules */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Package className="h-5 w-5" />
              Licensed Modules
            </CardTitle>
            <CardDescription>Modules included in your license</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {licenseStatus?.modules_enabled?.map((module) => (
                <span
                  key={module}
                  className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-medium"
                >
                  {module.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Upload License */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upgrade License
            </CardTitle>
            <CardDescription>Upload a new license file from AurousOrbit.ai</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed border-slate-600 rounded-lg p-6 text-center hover:border-emerald-500 transition-colors">
              <input
                type="file"
                id="license-upload"
                accept=".lic,.json"
                onChange={handleUploadLicense}
                className="hidden"
              />
              <label
                htmlFor="license-upload"
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                <FileText className="h-10 w-10 text-slate-400" />
                <span className="text-slate-400">Click to upload license file</span>
                <span className="text-xs text-slate-500">.lic or .json format</span>
              </label>
            </div>
            {uploading && (
              <div className="flex items-center justify-center gap-2 text-emerald-400">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Validating license...
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Event Log */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="h-5 w-5" />
            License Event Log
          </CardTitle>
          <CardDescription>Immutable audit trail of license events</CardDescription>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <p className="text-slate-400 text-center py-4">No events recorded</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {events.slice().reverse().map((event, index) => (
                <div
                  key={event.event_id || index}
                  className={`p-3 rounded-lg border ${
                    event.severity === 'critical'
                      ? 'bg-red-900/20 border-red-700'
                      : event.severity === 'warning'
                      ? 'bg-yellow-900/20 border-yellow-700'
                      : 'bg-slate-700/50 border-slate-600'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${
                      event.severity === 'critical'
                        ? 'text-red-400'
                        : event.severity === 'warning'
                        ? 'text-yellow-400'
                        : 'text-white'
                    }`}>
                      {event.event_type.replace(/_/g, ' ')}
                    </span>
                    <span className="text-xs text-slate-500">{event.timestamp?.split('T')[0]}</span>
                  </div>
                  {event.details && (
                    <p className="text-sm text-slate-400 mt-1">
                      {JSON.stringify(event.details).substring(0, 100)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Integrity Status */}
      {enforcementStatus?.integrity && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              {enforcementStatus.integrity.verified ? (
                <Lock className="h-5 w-5 text-emerald-500" />
              ) : (
                <Unlock className="h-5 w-5 text-red-500" />
              )}
              System Integrity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`flex items-center gap-2 ${
              enforcementStatus.integrity.verified ? 'text-emerald-400' : 'text-red-400'
            }`}>
              {enforcementStatus.integrity.verified ? (
                <CheckCircle className="h-5 w-5" />
              ) : (
                <XCircle className="h-5 w-5" />
              )}
              {enforcementStatus.integrity.message}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LicenseManagement;
